/**
 * Used for identifying appropriate plural rule when interpolating a message
 * Ideally this should be provided by some library or via React context
 */
const plurals = new Intl.PluralRules(`en-US`)

/**
 * Looks for plural placeholders, such as {count, plural, one {# apple} other {# apples}}
 * zero|one|two|few|many|other are the only allowed plural rules,
 * for more details see http://cldr.unicode.org/index/cldr-spec/plural-rules#TOC-Determining-Plural-Categories
 */
const PLURAL_PLACEHOLDERS_REGEX = /{([^,]+),\s+plural,(\s(?:zero|one|two|few|many|other)\s+{(?:[^}]*?)})+}/gi

/**
 * This function can substitute "placeholders" values in a string with provided replacements
 *
 * For example, if we had a message like this: "Connect to {cmsName}",
 * we could replace "{cmsName}" with an actual CMS name by passing it as the second parameter:
 *
 * const buttonlabel = interpolateMessage<'cmsName'>('Connect to {cmsName}', { cmsName: 'DatoCMS' })
 *
 * This helps us avoid splitting "rich" text copies into multiple messages
 *
 * Note that interpolateMessage accepts a type variable, TValues, which can be used to tell TypeScript
 * which values are expected to be passed to the function
 */
export function interpolateMessage<TValues = void>(
  message: string,
  values: InterpolationValues<TValues>["values"]
): string {
  if (!values || Object.keys(values as object).length === 0) {
    return message
  }

  // If values are provided, use them to replace value placeholders
  const messageWithReplacedValues = message.replace(/{(.*?)}/gi, substring => {
    const key = (substring.replace(/[{}]/g, "") as unknown) as TValues
    return values[key] ? String(values[key]) : substring
  })

  // Quick early check for potential plural placeholders
  if (!messageWithReplacedValues.includes(`plural`)) {
    return messageWithReplacedValues
  }

  return messageWithReplacedValues.replace(
    PLURAL_PLACEHOLDERS_REGEX,
    (substring, key: TValues) => {
      if (values[key] === undefined) {
        return substring
      }

      const value = values[key] as number | string
      const pluralRule = plurals.select(Number(value))
      // Search for the replacement defined for the current plural rule
      const ruleMatch = substring.match(
        new RegExp(`${pluralRule}\\s+{([^}]*?)}`, "i")
      )

      if (ruleMatch) {
        return ruleMatch[1].replace("#", String(value))
      }

      return substring
    }
  )
}

export type InterpolationValues<TValues> = [TValues] extends [never]
  ? { values?: undefined }
  : [TValues] extends [string | number]
  ? { values: Record<TValues, string | number> }
  : { values?: undefined }
